import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import { createContact } from "../api";
import { useAppDispatch, useAppSelector } from "../hooks";
import { setContacts } from "../features/appSlice";
import NotificationService from "../components/notification/NotificationService";

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
}

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
}

const ContactDialog: React.FC<ContactDialogProps> = ({ open, onClose }) => {
  const [contact, setContact] = useState<Contact>({
    id: crypto.randomUUID(),
    name: "",
    email: "",
    phone: "",
  });
  const dispatch = useAppDispatch();
  const contacts = useAppSelector((state) => state.app.contacts);

  const handleChange = (field: keyof Contact, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    createContact(contact)
      .then(() => {
        dispatch(setContacts([...contacts, contact]));
        NotificationService.showToast({
          severity: "success",
          text: "Kontaktperson blev oprettet",
        });
        handleClose();
      })
      .catch(() => {
        NotificationService.showToast({
          severity: "error",
          text: "Kontaktperson blev ikke oprettet",
        });
      });
  };

  // Reset contact when dialog is closed
  const handleClose = () => {
    setContact({
      id: crypto.randomUUID(),
      name: "",
      email: "",
      phone: "",
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Typography variant="h6" gutterBottom>
          Ny Kontaktperson
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Navn"
            value={contact.name}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            value={contact.email}
            onChange={(e) => handleChange("email", e.target.value)}
            fullWidth
          />
          <TextField
            label="Telefon"
            value={contact.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box>
          <Button onClick={handleClose} color="secondary">
            Annuller
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!contact.name || !contact.email || !contact.phone}
          >
            Opret
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDialog;
